import { PaymentIntent } from "@stripe/stripe-js"
import { AuthDialog } from "app-constants/constants"
import ConfirmationPaymentDialog from "components/conventions/registration/dialogs/ConfirmationPaymentDialog"
import ConventionVirtualRegistrationDialog from "components/conventions/registration/dialogs/ConventionVirtualRegistrationDialog"
import useAuthRedirect from "hooks/useAuthRedirect"
import useSearchParams from "hooks/useSearchParams"
import Convention from "models/convention-api/v1/Convention"
import ConventionRegistration from "models/convention-api/v1/ConventionRegistration"
import moment from "moment-timezone"
import React, { useRef, useState } from "react"
import { CurrentTimePhase, getCurrentTimePhase } from "utils/helpers"

interface OnRegisterPayload {
  convention: Convention
  conventionRegistration: ConventionRegistration
  paymentIntent: PaymentIntent | null
}

const useRegisterVirtualDialog = (onRegister: (payload: OnRegisterPayload) => void) => {
  const authRedirect = useAuthRedirect()
  const [convention, setConvention] = useState<Convention | null>(null)
  const [conventionRegistration, setConventionRegistration] =
    useState<ConventionRegistration | null>(null)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null)
  const onSuccessCallbackRef = useRef<(() => void) | null>(null)
  const searchParams = useSearchParams()

  const handleClose = async (clearSearchParams: boolean = true) => {
    setConvention(null)
    setConventionRegistration(null)
    setPaymentIntent(null)
    onSuccessCallbackRef.current = null

    if (clearSearchParams) await searchParams.clear()
  }

  const handleRegister = async (payload: OnRegisterPayload) => {
    setConvention(null)
    onRegister(payload)

    setConventionRegistration(payload.conventionRegistration)
    setPaymentIntent(payload.paymentIntent)

    if (onSuccessCallbackRef.current) onSuccessCallbackRef.current()
    if (!payload.paymentIntent) await handleClose()
  }

  const openDialog = (convention: Convention, onSuccess?: () => void) =>
    authRedirect(
      () => {
        setConvention(convention)
        onSuccessCallbackRef.current = onSuccess
      },
      {
        dialogToOpen: {
          dialog:
            getCurrentTimePhase(moment(convention.start_at), moment(convention.end_at)) ===
            CurrentTimePhase.DURING
              ? AuthDialog.JOIN
              : AuthDialog.SIGN_UP_WITHOUT_PASSWORD,
          payload: {
            conventionIdToRegister: convention.id,
            allRegistrantTypes: convention.automatically_generated,
          },
        },
      },
    )

  return {
    openDialog,
    Dialog: (
      <>
        {convention && !paymentIntent && (
          <ConventionVirtualRegistrationDialog
            convention={convention}
            onCancel={handleClose}
            onRegister={handleRegister}
          />
        )}
        {convention && conventionRegistration && paymentIntent && (
          <ConfirmationPaymentDialog
            convention={convention}
            conventionRegistration={conventionRegistration}
            paymentIntent={paymentIntent}
            onClose={handleClose}
          />
        )}
      </>
    ),
  }
}

export default useRegisterVirtualDialog
